import { alpha, Box, Typography, useTheme } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LuFileAudio } from "react-icons/lu";

interface RecordingCardProps {
  file: File | null;
  meta: string;
  name?: string;
  icon?: ReactNode;
  top?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}

/*
 * The recording as a tinted panel. It stays on screen from upload to result,
 * with the right-hand side changing as the job moves on.
 */
export const RecordingCard = ({
  file,
  meta,
  name,
  icon,
  top,
  children,
  footer,
}: RecordingCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        borderRadius: "20px",
        px: 2.5,
        py: 2,
      }}
    >
      {top && <Box sx={{ mb: 2 }}>{top}</Box>}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          aria-hidden
          sx={{ display: "flex", color: theme.palette.primary.main }}
        >
          {icon ?? <LuFileAudio size={28} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography
            sx={{ fontSize: 15, fontWeight: 600, overflowWrap: "anywhere" }}
          >
            {name ?? file?.name ?? t("audio.recording.fallbackName")}
          </Typography>
          {meta && (
            <Typography
              sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}
            >
              {meta}
            </Typography>
          )}
        </Box>
        {children && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              ml: { md: "auto" },
            }}
          >
            {children}
          </Box>
        )}
      </Box>
      {footer && <Box sx={{ mt: 2 }}>{footer}</Box>}
    </Box>
  );
};
